const Url = require('url');
const querystring = require('querystring');
const util = require('./util');
const debug = require('debug')('plugin:Router');
const ROUTING = require('./const/routing');

class Router {
	constructor({window, onPopStateHook, routingMethod}) {
		this.window = window;
		this.currentLocation = '';
		this.pushStateEnabled = false;
		this.onPopStateHook = onPopStateHook;
		this.routingMethod = routingMethod;
	}

	init({baseUrl, basePath}) {
		this.setBaseUrl(baseUrl);
		this.setBasePath(basePath);

		if (typeof this.window.history.pushState === 'function') {
			this.pushStateEnabled = true;
			this.setupHistory();
		}
	}

	setBasePath(_path='/') {
		this.basePath = util.trimSlash(_path);
	}

	setBaseUrl(_url='') {
		if (_url.indexOf('http') !== 0) {
			throw new Error('Base URL must be a full url with protocol (http/https)');
		}
		// Set with no trailing slashes
		this.baseUrl = util.trimSlash(_url);
		const url = Url.parse(_url);
		this.baseHost = url.host;
		this.baseProtocol = url.protocol;
	}

	/**
	 * @description Route to a new URL
	 * @param {string} _url URL to route to
	 * @param {object} [opts={}]
	 * @param {boolean} [opts.force=false] Force routing ignoring routing rules
	 * @param {boolean} [opts.shouldPushState=true] Add to browser history
	 * @param {boolean} [opts.preventNavigation=false] Do everything but load the route
	 * @returns {Promise} On resolve, we've routed
	 * @memberof Router
	 */
	go(_url, opts={}) {
		return new Promise((resolve, reject) => {
			const {force=false, shouldPushState=true, preventNavigation=false} = opts;
			const url = `${_url}`;
			const shouldRunRoute = !this.shouldNotRoute(url);
			// Use replace state if first load ('') or same url as current or push set to false
			const shouldReplaceState = (
				!shouldPushState
				|| this.currentLocation === ''
				|| this.currentLocation === url
			);

			debug('go', {url, shouldRunRoute, shouldReplaceState, preventNavigation, force});
			if (shouldRunRoute || force) {
				this.currentLocation = url;

				if (shouldReplaceState) {
					this.replaceState({url});
				} else {
					this.pushPushState({url});
				}
				// If preventNavigation is true, just update the path in the URL bar
				if (!preventNavigation) {
					resolve({url});
				} else {
					reject({url, error: 'navigation_prevented'});
				}
			} else {
				reject({url, error: 'did_not_route'});
			}
		});
	}

	getWebstoreUrlFromClientUrl(_url) {
		const cleanUrl = _url.replace(this.baseUrl, '').replace(this.basePath, '');
		const parsedUrl = Url.parse(cleanUrl || '/');
		parsedUrl.href = '';
		parsedUrl.pathname = parsedUrl.pathname || '/';
		if (parsedUrl.pathname.indexOf('/') !== 0) {
			parsedUrl.pathname = `/${parsedUrl.pathname}`;
		}
		const url = Url.format(parsedUrl);

		if (this.routingMethod !== ROUTING.types.path) {
			const parsedQuery = querystring.parse(parsedUrl.query);
			if (parsedQuery[ROUTING.query.key]) {
				return util.trimSlash(decodeURIComponent(parsedQuery[ROUTING.query.key]));
			}
		}

		return util.trimSlash(url);
	}

	isPushStateEnabled() {
		return this.pushStateEnabled;
	}

	shouldNotRoute(_url) {
		const url = `${_url}`;
		return (
			url.indexOf('javascript:') !== -1 ||
			url.indexOf('#') === 0 ||
			util.trimSlash(this.getCurrentLocation()) === util.trimSlash(url)
		);
	}

	getCurrentLocation() {
		return this.currentLocation;
	}

	setupHistory() {
		const window = this.window;
		debug('setupHistory', window.history);
		// window.addEventListener('popstate', this.handleHistoryPop.bind(this));
		window.onpopstate = this.handleHistoryPop.bind(this);
	}

	pushPushState({url:_url}) {
		const window = this.window;
		const {state, title, url} = this.generatePushState({url: _url});

		if (url) {
			window.history.pushState(state, title, url);
		}
	}

	replaceState({url:_url}) {
		const window = this.window;
		const {state, title, url} = this.generatePushState({url: _url});

		if (url) {
			window.history.replaceState(state, title, url);
		}
	}

	generatePushState({url:_url}) {
		const state = {};
		let title = '';
		let url = _url;

		debug('generatePushState', {state, title, url});

		return {state, title, url};
	}

	addPopTransform({fn}) {
		this.onPopTransform.push(fn);
	}

	handleHistoryPop(popEvent) {
		debug('handleHistoryPop', popEvent);
		let href = this.window.location.href;

		if (href && this.onPopStateHook) {
			this.onPopStateHook(this.go(href, {
				shouldPushState: false,
			}));
		}
	}
}

module.exports = Router;
